/**
 * useCreateOrderingPacket — shared hook for "Send to Ordering" submissions.
 *
 * Centralizes packet creation so every entry point (Job Detail, Jobs List,
 * Operations Command Center, Dispatch Intelligence, WhatNeedsAttentionStrip)
 * creates a packet without duplicating the call.
 *
 * Returns a function that accepts the OrderingModal's submission payload and:
 *   1. Resolves the Job from AppData
 *   2. Creates an OrderingPacket via OrderingPacketContext
 *   3. Returns the packet (caller can navigate to it if desired)
 *
 * Existing job-mutation logic (planningStatus → "Ordered", activity history,
 * vendor selection) remains in each call-site handler — this hook is purely
 * additive and creates the manual-rep workflow record.
 */
import { useCallback } from "react";
import { useAppData } from "@/data/AppDataContext";
import { useRole } from "@/data/RoleContext";
import { useOrderingPacket } from "@/data/orderingPacket/context";

export interface SendToOrderingPayload {
  jobId: string;
  vendor: string;
  orderType: "Batch" | "Direct" | "Rush";
  notes: string;
}

export function useCreateOrderingPacket() {
  const { jobs } = useAppData();
  const { createPacket } = useOrderingPacket();
  const { role, roleInfo } = useRole();
  const sentBy = roleInfo?.label || role || "Ops Rep";

  return useCallback(
    (data: SendToOrderingPayload) => {
      const job = jobs.find((j) => j.id === data.jobId);
      if (!job) return null;
      return createPacket({
        job,
        intendedVendor: data.vendor,
        orderType: data.orderType,
        notes: data.notes,
        sentBy,
      });
    },
    [jobs, createPacket, sentBy],
  );
}